import { useState, useEffect, useCallback, useRef } from "react";
import Hls from "hls.js";
import { useAuth } from "@/context/AuthContext";
import { soApi } from "@/webapp/site-owners/api";
import { Navigate } from "react-router-dom";
import SecurityCenter from "@/webapp/security/SecurityCenter";
import GatePanel, { PlateEdit } from "@/webapp/site-owners/GatePanel";
import "@/webapp/site-owners/console.css";

const NAV = [
  { id: "portfolio", label: "Portfolio" },
  { id: "release", label: "Release Capacity" },
  { id: "bookings", label: "Bookings" },
  { id: "customers", label: "Customers" },
  { id: "calendar", label: "Calendar" },
  { id: "arrival", label: "Arrival" },
  { id: "overflow", label: "Overflow" },
  { id: "maintenance", label: "Maintenance" },
  { id: "billing", label: "Billing" },
  { id: "analytics", label: "Analytics" },
  { id: "alerts", label: "Alerts" },
  { id: "settlement", label: "Settlement" },
  { id: "security", label: "Security" },
];

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const STATUS_TONE = {
  booked: "text-amber-700 bg-amber-50", verified: "text-blue-700 bg-blue-50", onsite: "text-emerald-700 bg-emerald-50",
  completed: "text-slate-500 bg-slate-100", cancelled: "text-red-600 bg-red-50",
};
const STALL_DOT = { available: "bg-emerald-500", released: "bg-emerald-500", booked: "bg-amber-500", onsite: "bg-red-500", leased: "bg-slate-400", oos: "bg-slate-600" };

const Card = ({ children, className = "", testid, ...rest }) => (
  <div data-testid={testid} className={`rounded-2xl border border-slate-200/70 bg-white shadow-[0_1px_2px_rgba(10,35,74,0.04),0_14px_34px_-20px_rgba(10,35,74,0.22)] ${className}`} {...rest}>{children}</div>
);
const Label = ({ children }) => <span className="mb-1 block text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">{children}</span>;
const Field = (p) => <input {...p} className="h-10 w-full rounded-lg border border-slate-200 bg-[#F7F8FA] px-3 text-sm text-[#0A234A] outline-none transition-all duration-200 focus:border-[#FF5722]/60 focus:bg-white focus:ring-4 focus:ring-[#FF5722]/10" />;
const Select = (p) => <select {...p} className="h-10 w-full rounded-lg border border-slate-200 bg-[#F7F8FA] px-3 text-sm text-[#0A234A] outline-none transition-all duration-200 focus:border-[#FF5722]/60 focus:bg-white focus:ring-4 focus:ring-[#FF5722]/10" />;
const Btn = ({ children, primary, ghost, testid, ...p }) => (
  <button data-testid={testid} {...p}
    className={`rounded-lg px-4 py-2 text-sm font-bold transition-all duration-200 active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5722]/40 disabled:opacity-40 ${primary ? "bg-[#FF5722] text-white shadow-[0_6px_16px_-6px_rgba(255,87,34,0.65)] hover:bg-[#e64a16] hover:shadow-[0_10px_22px_-6px_rgba(255,87,34,0.7)]" : ghost ? "text-[#0A234A] hover:bg-slate-100" : "border border-slate-300 bg-white text-[#0A234A] hover:border-slate-400 hover:bg-slate-50"}`}>{children}</button>
);
const Head = ({ title, sub, children }) => (
  <div className="mb-7 flex flex-wrap items-end justify-between gap-3 border-b border-slate-200/80 pb-5">
    <div>
      <span className="mb-2 block h-1 w-9 rounded-full bg-[#FF5722]" />
      <h1 className="font-display text-3xl font-bold leading-[1.05] tracking-tight text-[#0A234A]">{title}</h1>
      {sub && <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-slate-500">{sub}</p>}
    </div>
    {children && <div className="flex items-center gap-2">{children}</div>}
  </div>
);
const Pill = ({ children, tone }) => <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${tone}`}>{children}</span>;
const Dot = ({ s }) => <span className={`inline-block h-2 w-2 rounded-full ${STALL_DOT[s] || "bg-slate-300"}`} />;

const YARD_LEGEND = [
  ["Available", "border-emerald-500"],
  ["Occupied", "border-red-500"],
  ["Reserved", "border-amber-500"],
  ["Out of Service", "border-slate-400"],
];
const YARD_IMG = { north: "/yard-north.png", central: "/yard-central.png", south: "/yard-south.png" };
const SITE_GEO = {
  north: { lat: 43.80, lon: -79.42 },
  central: { lat: 43.6532, lon: -79.3832 },
  south: { lat: 43.59, lon: -79.64 },
};
const STALL_LAYOUT = {
  north: { top: 30, left: 24, width: 54, height: 36, cols: 6 },
  central: { top: 20, left: 12, width: 76, height: 52, cols: 4 },
  south: { top: 18, left: 8, width: 66, height: 46, cols: 5 },
};
const WCODE = {
  0: ["Clear", "bg-amber-400"], 1: ["Mainly clear", "bg-amber-300"], 2: ["Partly cloudy", "bg-slate-300"], 3: ["Overcast", "bg-slate-400"],
  45: ["Fog", "bg-slate-300"], 48: ["Rime fog", "bg-slate-300"], 51: ["Light drizzle", "bg-sky-300"], 53: ["Drizzle", "bg-sky-400"], 55: ["Heavy drizzle", "bg-sky-500"],
  61: ["Light rain", "bg-sky-400"], 63: ["Rain", "bg-sky-500"], 65: ["Heavy rain", "bg-sky-600"], 66: ["Freezing rain", "bg-sky-500"], 67: ["Freezing rain", "bg-sky-600"],
  71: ["Light snow", "bg-sky-200"], 73: ["Snow", "bg-sky-200"], 75: ["Heavy snow", "bg-sky-100"], 77: ["Snow grains", "bg-sky-100"],
  80: ["Rain showers", "bg-sky-500"], 81: ["Showers", "bg-sky-500"], 82: ["Violent showers", "bg-sky-600"], 85: ["Snow showers", "bg-sky-200"], 86: ["Snow showers", "bg-sky-100"],
  95: ["Thunderstorm", "bg-indigo-500"], 96: ["Storm + hail", "bg-indigo-600"], 99: ["Storm + hail", "bg-indigo-700"],
};
function resolveStall(stall, booking) {
  if (stall.status === "oos") return { label: "Out of service", chip: "border-slate-400 bg-slate-200 text-slate-600", ring: "ring-slate-400" };
  if (booking && booking.status === "onsite") return { label: "Occupied", chip: "border-red-500 bg-red-50 text-red-700", ring: "ring-red-500", booking };
  if (booking && (booking.status === "booked" || booking.status === "verified")) return { label: "Reserved", chip: "border-amber-500 bg-amber-50 text-amber-700", ring: "ring-amber-500", booking };
  if (stall.status === "leased") return { label: "Under lease", chip: "border-slate-300 bg-slate-100 text-slate-500", ring: "ring-slate-400" };
  if (stall.status === "released") return { label: "Listed — available", chip: "border-emerald-500 bg-emerald-50 text-emerald-700", ring: "ring-emerald-500" };
  return { label: "Available", chip: "border-emerald-500 bg-emerald-50 text-emerald-700", ring: "ring-emerald-500" };
}
function YardOverview({ data, terminal, setTerminal }) {
  const [full, setFull] = useState(false);
  const [sel, setSel] = useState(null);
  const [showOverlay, setShowOverlay] = useState(true);
  const [weather, setWeather] = useState(null);
  const siteName = (id) => data.sites.find((s) => s.id === id)?.name || "Terminal";
  const mapSrc = YARD_IMG[terminal] || YARD_IMG.north;
  const stalls = data.stalls.filter((s) => s.site === terminal);
  const bookingFor = (code) => data.bookings.find((b) => b.site === terminal && b.stall === code && b.status !== "cancelled");
  useEffect(() => { setSel(null); }, [terminal]);
  useEffect(() => {
    const geo = SITE_GEO[terminal] || SITE_GEO.north; let active = true; setWeather(null);
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${geo.lat}&longitude=${geo.lon}&current=temperature_2m,weather_code&hourly=temperature_2m,weather_code&forecast_hours=12&timezone=auto`)
      .then((r) => r.json()).then((d) => {
        if (!active || !d || !d.current) return;
        let hours = [];
        if (d.hourly && Array.isArray(d.hourly.time)) {
          const nowT = new Date(d.current.time).getTime();
          hours = d.hourly.time
            .map((t, i) => ({ t, temp: Math.round(d.hourly.temperature_2m[i]), code: d.hourly.weather_code[i] }))
            .filter((h) => new Date(h.t).getTime() > nowT)
            .slice(0, 6);
        }
        setWeather({ temp: Math.round(d.current.temperature_2m), code: d.current.weather_code, hours });
      })
      .catch(() => {});
    return () => { active = false; };
  }, [terminal]);
  const wx = weather ? (WCODE[weather.code] || ["—", "bg-slate-300"]) : null;
  const hourLabel = (t) => new Date(t).toLocaleTimeString([], { hour: "numeric" });
  const selStall = sel ? stalls.find((s) => s.code === sel) : null;
  const selInfo = selStall ? resolveStall(selStall, bookingFor(selStall.code)) : null;
  return (
    <Card testid="yard-overview-card" className="w-full max-w-2xl overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 px-5 py-4 sm:px-6">
        <h2 className="font-display text-xl font-bold tracking-tight text-[#0A234A]">Yard Overview</h2>
        <span className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-600" data-testid="yard-live">
          <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" /></span>
          Live
        </span>
        <div className="ml-auto flex items-center gap-4">
          <span className="hidden items-center gap-2 text-sm font-semibold text-slate-500 sm:flex" data-testid="yard-weather">
            {weather ? (<><span className={`h-2.5 w-2.5 rounded-full ${wx[1]}`} /><span className="font-bold text-[#0A234A]">{weather.temp}°C</span> {wx[0]}</>) : <span className="text-slate-300">Loading weather…</span>}
          </span>
          <select data-testid="yard-terminal-select" value={terminal} onChange={(e) => setTerminal(e.target.value)}
            className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-bold text-[#0A234A] outline-none transition-colors hover:border-slate-300 focus:border-[#FF5722]/60 focus:ring-4 focus:ring-[#FF5722]/10">
            {data.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>
      {weather && weather.hours && weather.hours.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto border-t border-slate-100 bg-slate-50/60 px-5 py-2.5 sm:px-6" data-testid="yard-forecast">
          <span className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide text-slate-400">Next hours</span>
          {weather.hours.map((h) => {
            const hw = WCODE[h.code] || ["", "bg-slate-300"];
            return (
              <span key={h.t} className="flex flex-shrink-0 items-center gap-1.5 rounded-md bg-white px-2 py-1 text-xs font-semibold text-slate-500 shadow-[0_1px_2px_rgba(10,35,74,0.05)]" title={hw[0]}>
                {hourLabel(h.t)}<span className={`h-2 w-2 rounded-full ${hw[1]}`} /><span className="font-bold text-[#0A234A]">{h.temp}°</span>
              </span>
            );
          })}
        </div>
      )}
      <div className="relative overflow-hidden border-y border-slate-200">
        <img src={mapSrc} alt={`Live yard map — ${siteName(terminal)}`} loading="lazy" className="block w-full select-none" draggable={false} />
        {showOverlay && (() => {
          const L = STALL_LAYOUT[terminal] || STALL_LAYOUT.north;
          const rows = Math.max(1, Math.ceil(stalls.length / L.cols));
          return (
            <div className="absolute inset-0" data-testid="yard-hotspots">
              {stalls.map((s, idx) => {
                const col = idx % L.cols, row = Math.floor(idx / L.cols);
                const x = L.left + ((col + 0.5) / L.cols) * L.width;
                const y = L.top + ((row + 0.5) / rows) * L.height;
                const info = resolveStall(s, bookingFor(s.code));
                const on = sel === s.code;
                return (
                  <button key={s.code} data-testid={`yard-hotspot-${s.code}`} onClick={() => setSel(on ? null : s.code)} title={`${s.code} · ${info.label}`}
                    style={{ left: `${x}%`, top: `${y}%` }}
                    className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-md border px-1.5 py-0.5 font-mono text-[10px] font-bold shadow-md backdrop-blur-[2px] transition-all ${info.chip} ${on ? `z-10 scale-125 ring-2 ${info.ring}` : "opacity-90 hover:z-10 hover:scale-125 hover:opacity-100"}`}>
                    {s.code}
                  </button>
                );
              })}
            </div>
          );
        })()}
        <button data-testid="yard-overlay-toggle" onClick={() => setShowOverlay((v) => !v)}
          className="absolute bottom-3 left-3 rounded-lg border border-slate-200 bg-white/95 px-3 py-2 text-sm font-bold text-[#0A234A] shadow-lg backdrop-blur transition-all hover:bg-white active:scale-[0.97]">
          {showOverlay ? "Hide stalls" : "Show stalls"}
        </button>
        <button data-testid="yard-view-full-map" onClick={() => setFull(true)}
          className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white/95 px-3.5 py-2 text-sm font-bold text-[#0A234A] shadow-lg backdrop-blur transition-all hover:bg-white active:scale-[0.97]">
          <span className="text-[#FF5722]">⤢</span> View Full Map
        </button>
      </div>
      <div className="px-5 pt-4 sm:px-6">
        <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">Stalls — tap for detail</p>
        <div className="flex flex-wrap gap-1.5" data-testid="yard-stall-grid">
          {stalls.map((s) => {
            const info = resolveStall(s, bookingFor(s.code));
            const on = sel === s.code;
            return (
              <button key={s.code} data-testid={`yard-stall-${s.code}`} onClick={() => setSel(on ? null : s.code)}
                className={`rounded-md border px-2 py-1 font-mono text-xs font-bold transition-all ${info.chip} ${on ? `ring-2 ${info.ring} scale-105` : "hover:scale-105"}`}>
                {s.code}
              </button>
            );
          })}
        </div>
        {selStall && (
          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/70 p-4" data-testid="yard-stall-detail">
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm font-bold text-[#0A234A]">{selStall.code}</span>
              <Pill tone={selInfo.chip.replace(/border-\S+\s?/, "")}>{selInfo.label}</Pill>
            </div>
            {selInfo.booking ? (
              <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div><Label>Tenant</Label><span className="font-semibold text-[#0A234A]">{selInfo.booking.party}</span></div>
                <div><Label>Vehicle</Label><span className="font-semibold text-[#0A234A]">{selInfo.booking.vehicle || "—"}</span></div>
                <div><Label>Channel</Label><span className="font-semibold text-[#0A234A]">{selInfo.booking.channel}</span></div>
                <div><Label>Booking</Label><span className="font-mono font-semibold text-[#0A234A]">{selInfo.booking.ref} · {money(selInfo.booking.amount)}</span></div>
              </div>
            ) : (
              <p className="mt-2 text-sm text-slate-500">{selStall.status === "leased" ? "Protected lease — not on the marketplace." : selStall.status === "released" ? "Listed for release — open to the marketplace." : selStall.status === "oos" ? "Out of service for maintenance." : "Open — available to release to the marketplace."}</p>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-5 pb-5 pt-4 sm:px-6">
        {YARD_LEGEND.map(([label, border]) => (
          <span key={label} className="flex items-center gap-2 text-sm font-semibold text-slate-500">
            <span className={`h-3.5 w-3.5 rounded-full border-2 bg-white ${border}`} />{label}
          </span>
        ))}
      </div>
      {full && (
        <div data-testid="yard-full-map-modal" onClick={() => setFull(false)} className="fixed inset-0 z-[60] flex items-center justify-center bg-[#0A234A]/80 p-4 backdrop-blur-sm sm:p-10">
          <div className="relative w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-white">{siteName(terminal)} — Live Yard Map</h3>
              <button data-testid="yard-full-map-close" onClick={() => setFull(false)} className="rounded-lg bg-white/10 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/20">Close ✕</button>
            </div>
            <img src={mapSrc} alt="Full yard map" className="w-full rounded-xl border border-white/10 shadow-2xl" />
            <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
              {YARD_LEGEND.map(([label, border]) => (
                <span key={label} className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                  <span className={`h-3.5 w-3.5 rounded-full border-2 bg-white ${border}`} />{label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

const CAMS = ["Gate Cam", "Yard Cam", "Dock Cam"];
const DEFAULT_STREAM = "https://demo.unified-streaming.com/k8s/features/stable/video/tears-of-steel/tears-of-steel.ism/.m3u8";
const PUBLIC_FEEDS = [
  "https://cph-p2p-msl.akamaized.net/hls/live/2000341/test/master.m3u8",
  "https://rbmn-live.akamaized.net/hls/live/590964/BoRB-AT/master.m3u8",
  "https://dwamdstream102.akamaized.net/hls/live/2015525/dwstream102/index.m3u8",
];
function CamStream({ terminal, cam, siteName, compact = false, showEditor = true }) {
  const videoRef = useRef(null);
  const [clock, setClock] = useState(() => new Date());
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState("");
  const streamKey = `so_cam_${terminal}_${cam}`;
  const camDefault = PUBLIC_FEEDS[cam] || DEFAULT_STREAM;
  const [streamUrl, setStreamUrl] = useState(camDefault);

  useEffect(() => { try { setStreamUrl(localStorage.getItem(streamKey) || camDefault); } catch { setStreamUrl(camDefault); } setEditing(false); }, [streamKey]);
  useEffect(() => { const id = setInterval(() => setClock(new Date()), 1000); return () => clearInterval(id); }, []);
  useEffect(() => {
    const video = videoRef.current; if (!video || !streamUrl) return;
    setErr(""); let hls;
    if (streamUrl.includes(".m3u8")) {
      if (video.canPlayType("application/vnd.apple.mpegurl")) { video.src = streamUrl; }
      else if (Hls.isSupported()) {
        hls = new Hls({ liveDurationInfinity: true, enableWorker: true });
        hls.loadSource(streamUrl); hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (_e, d) => {
          if (!d.fatal) return;
          if (d.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
          else if (d.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
          else { try { hls.destroy(); } catch {} setErr("Stream unavailable — check the camera URL."); }
        });
      } else { setErr("HLS playback not supported in this browser."); }
    } else { video.src = streamUrl; }
    video.play?.().catch(() => {});
    return () => { if (hls) hls.destroy(); };
  }, [streamUrl]);

  const saveUrl = () => { const u = draft.trim(); try { if (u) localStorage.setItem(streamKey, u); else localStorage.removeItem(streamKey); } catch {} setStreamUrl(u || DEFAULT_STREAM); setEditing(false); };
  const useDemo = () => { try { localStorage.removeItem(streamKey); } catch {} setStreamUrl(camDefault); setEditing(false); };
  const ts = clock.toLocaleString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).replace(",", "");
  const tsShort = clock.toLocaleTimeString("en-GB", { hour12: false });
  const isDemo = streamUrl === camDefault;
  let host = ""; try { host = new URL(streamUrl).host; } catch { host = "—"; }

  return (
    <div className="flex flex-col">
      <div className={`relative aspect-video w-full overflow-hidden bg-black ${compact ? "rounded-lg border border-slate-800" : "border-y border-slate-200"}`} data-testid={`camera-tile-${cam}`}>
        <video ref={videoRef} data-testid={`camera-video-${cam}`} muted autoPlay loop playsInline className="h-full w-full object-cover contrast-[1.05] saturate-[0.95]" />
        <div className="pointer-events-none absolute inset-0" style={{ background: "repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0px, rgba(0,0,0,0) 2px, rgba(0,0,0,0.08) 3px)" }} />
        <div className="pointer-events-none absolute inset-0" style={{ boxShadow: "inset 0 0 120px rgba(0,0,0,0.5)" }} />
        <div className={`absolute left-2 top-2 flex items-center gap-1.5 rounded bg-black/55 px-1.5 py-0.5 font-mono font-bold text-white backdrop-blur-sm ${compact ? "text-[10px]" : "text-xs sm:left-3 sm:top-3 sm:px-2 sm:py-1"}`}>
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" /> {CAMS[cam]}{compact ? "" : ` · ${siteName}`}
        </div>
        <div className={`absolute right-2 top-2 rounded bg-black/55 px-1.5 py-0.5 font-mono font-semibold text-white/90 backdrop-blur-sm ${compact ? "text-[9px]" : "text-xs sm:right-3 sm:top-3 sm:px-2 sm:py-1"}`} data-testid={`camera-timestamp-${cam}`}>{compact ? tsShort : ts}</div>
        <div className={`absolute bottom-2 left-2 font-mono font-bold tracking-widest text-white/80 ${compact ? "text-[9px]" : "text-[11px] sm:bottom-3 sm:left-3"}`}>CAM 0{cam + 1}</div>
        {err && <div className="absolute inset-x-0 bottom-0 bg-red-600/90 px-2 py-1 text-center text-[10px] font-bold text-white sm:text-xs" data-testid={`camera-error-${cam}`}>{err}</div>}
      </div>
      {showEditor && (
        <>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3 text-xs sm:px-6">
            <span className="font-semibold text-slate-400">{isDemo ? "Public live · " : "Source · "}<span className="font-mono text-slate-500">{host}</span></span>
            {!editing && <button data-testid="camera-set-url" onClick={() => { setDraft(isDemo ? "" : streamUrl); setEditing(true); }} className="ml-auto font-bold text-[#FF5722] hover:underline">Connect camera</button>}
          </div>
          {editing && (
            <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-5 py-3 sm:px-6">
              <input data-testid="camera-url-input" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveUrl()} placeholder="HLS (.m3u8) or stream URL for this camera"
                className="h-9 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm text-[#0A234A] outline-none focus:border-[#FF5722]/60 focus:ring-4 focus:ring-[#FF5722]/10" />
              <button data-testid="camera-url-save" onClick={saveUrl} className="h-9 rounded-lg bg-[#0A234A] px-3.5 text-sm font-bold text-white hover:bg-[#0A234A]/90">Connect</button>
              <button data-testid="camera-url-demo" onClick={useDemo} className="h-9 rounded-lg border border-slate-200 px-3.5 text-sm font-bold text-slate-500 hover:bg-slate-50">Public feed</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
function CameraFeed({ data, terminal }) {
  const [mode, setMode] = useState("single");
  const [cam, setCam] = useState(0);
  const siteName = data.sites.find((s) => s.id === terminal)?.name || "Terminal";
  const toggle = (m) => `rounded-md px-3 py-1 text-xs font-bold transition-colors ${mode === m ? "bg-[#0A234A] text-white" : "text-slate-500 hover:text-[#0A234A]"}`;
  return (
    <Card testid="camera-card" className="w-full max-w-2xl overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 px-5 py-4 sm:px-6">
        <h2 className="font-display text-xl font-bold tracking-tight text-[#0A234A]">Live Camera</h2>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-bold text-red-600" data-testid="camera-rec">
          <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" /></span>
          LIVE
        </span>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            <button data-testid="camera-mode-single" onClick={() => setMode("single")} className={toggle("single")}>Single</button>
            <button data-testid="camera-mode-grid" onClick={() => setMode("grid")} className={toggle("grid")}>All cameras</button>
          </div>
          {mode === "single" && (
            <select data-testid="camera-select" value={cam} onChange={(e) => setCam(Number(e.target.value))}
              className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-bold text-[#0A234A] outline-none transition-colors hover:border-slate-300 focus:border-[#FF5722]/60 focus:ring-4 focus:ring-[#FF5722]/10">
              {CAMS.map((label, i) => <option key={label} value={i}>{label}</option>)}
            </select>
          )}
        </div>
      </div>
      {mode === "single" ? (
        <CamStream key={`single-${terminal}-${cam}`} terminal={terminal} cam={cam} siteName={siteName} compact={false} showEditor />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 bg-slate-900 p-2" data-testid="camera-grid">
            {[0, 1, 2].map((i) => <CamStream key={`grid-${terminal}-${i}`} terminal={terminal} cam={i} siteName={siteName} compact showEditor={false} />)}
            <div className="flex aspect-video items-center justify-center rounded-lg border border-dashed border-slate-700 text-xs font-semibold text-slate-500">+ Add camera</div>
          </div>
          <p className="px-5 py-3 text-xs text-slate-400 sm:px-6">All cameras at {siteName} · connect each feed in Single view.</p>
        </>
      )}
    </Card>
  );
}

function YardActivity({ data, terminal }) {
  const siteName = data.sites.find((s) => s.id === terminal)?.name || "Terminal";
  const siteBookings = data.bookings.filter((b) => b.site === terminal && b.status !== "cancelled");
  const parked = siteBookings.filter((b) => b.status === "onsite").sort((a, b) => String(a.stall).localeCompare(String(b.stall)));
  const SCHED_TONE = { booked: "bg-amber-50 text-amber-700", verified: "bg-indigo-50 text-indigo-700", onsite: "bg-emerald-50 text-emerald-700", departed: "bg-slate-100 text-slate-500", completed: "bg-slate-100 text-slate-500" };
  const fmt = (t) => t ? new Date(t).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : null;
  const schedule = siteBookings
    .map((b) => ({ ...b, _t: b.entry_at || b.created_at || "" }))
    .sort((a, b) => String(a._t).localeCompare(String(b._t)));
  return (
    <Card testid="yard-activity-card" className="flex w-full flex-col overflow-hidden lg:min-w-0 lg:flex-1">
      <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-5 py-4">
        <div>
          <h2 className="font-display text-base font-bold text-[#0A234A]">Yard Activity</h2>
          <p className="text-xs font-semibold text-slate-400">{siteName} · today</p>
        </div>
        <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-600">
          <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></span>
          {parked.length} on site
        </span>
      </div>
      <div className="px-5 py-4">
        <p className="mb-2.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">Parked now</p>
        <ul className="space-y-1" data-testid="yard-parked-list">
          {parked.map((b) => (
            <li key={b.id} className="flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-slate-50" data-testid={`yard-parked-${b.ref}`}>
              <span className="w-11 flex-shrink-0 rounded-md bg-[#0A234A] py-1 text-center font-mono text-xs font-bold text-white">{b.stall}</span>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[#0A234A]">{b.vehicle || b.party}</span>
              <span className="truncate text-xs font-medium text-slate-400">{b.party}</span>
            </li>
          ))}
          {parked.length === 0 && <li className="rounded-lg bg-slate-50 px-3 py-2.5 text-sm text-slate-400">No trucks parked right now.</li>}
        </ul>
      </div>
      <div className="border-t border-slate-100 px-5 py-4">
        <p className="mb-3 text-[11px] font-bold uppercase tracking-wide text-slate-400">Today's schedule</p>
        <ul className="space-y-0" data-testid="yard-schedule-list">
          {schedule.map((b, i) => (
            <li key={b.id} className={`flex items-center gap-3 py-2 ${i !== schedule.length - 1 ? "border-b border-slate-50" : ""}`} data-testid={`yard-sched-${b.ref}`}>
              <span className="w-14 flex-shrink-0 font-mono text-xs font-semibold text-slate-500">{fmt(b.entry_at) || "TBD"}</span>
              <span className="w-11 flex-shrink-0 rounded-md border border-slate-200 py-1 text-center font-mono text-xs font-bold text-[#0A234A]">{b.stall}</span>
              <span className="min-w-0 flex-1 truncate text-sm text-slate-600"><span className="font-semibold text-[#0A234A]">{b.party}</span>{b.exit_at ? ` · out ${fmt(b.exit_at)}` : ""}</span>
              <Pill tone={SCHED_TONE[b.status] || "bg-slate-100 text-slate-500"}>{b.status}</Pill>
            </li>
          ))}
          {schedule.length === 0 && <li className="rounded-lg bg-slate-50 px-3 py-2.5 text-sm text-slate-400">Nothing scheduled for this yard today.</li>}
        </ul>
      </div>
    </Card>
  );
}

function Portfolio({ data }) {
  const [terminal, setTerminal] = useState(data.sites[0]?.id || "north");
  const net = data.sites.reduce((a, s) => ({ available: a.available + s.available, reserved: a.reserved + s.reserved, occupied: a.occupied + s.occupied, leased: a.leased + s.leased, total: a.total + s.total }), { available: 0, reserved: 0, occupied: 0, leased: 0, total: 0 });
  return (
    <>
      <Head title="Portfolio" sub="Your network at a glance — available, reserved and occupied capacity across every site." />
      <div className="mb-6 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-4">
        {[["Available", net.available, "text-emerald-600"], ["Reserved", net.reserved, "text-amber-600"], ["Occupied", net.occupied, "text-red-600"], ["Under lease", net.leased, "text-slate-500"]].map(([l, v, c]) => (
          <div key={l} className="bg-white p-5"><Label>{l}</Label><p className={`font-display text-4xl font-bold ${c}`}>{v}</p><p className="mt-1 text-xs text-slate-400">of {net.total} stalls</p></div>
        ))}
      </div>
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="flex w-full flex-col gap-6 lg:w-auto">
          <YardOverview data={data} terminal={terminal} setTerminal={setTerminal} />
          <CameraFeed data={data} terminal={terminal} />
        </div>
        <div className="flex w-full flex-col gap-3 lg:w-64 lg:flex-shrink-0">
          {data.sites.map((s) => {
            const util = s.total ? Math.round(((s.reserved + s.occupied) / s.total) * 100) : 0;
            return (
              <Card key={s.id} testid={`so-site-${s.id}`} className={`cursor-pointer p-4 transition-all ${terminal === s.id ? "ring-2 ring-[#FF5722]/60" : "hover:ring-1 hover:ring-slate-200"}`} onClick={() => setTerminal(s.id)}>
                <div className="flex items-baseline justify-between gap-2"><h2 className="font-display text-base font-bold text-[#0A234A]">{s.name}</h2><span className="truncate text-[10px] font-semibold text-slate-400">{s.address}</span></div>
                <div className="mt-3 space-y-1.5 text-xs">
                  {[["Available", s.available, "available"], ["Reserved", s.reserved, "booked"], ["Occupied", s.occupied, "onsite"], ["Under lease", s.leased, "leased"]].map(([l, v, st]) => (
                    <div key={l} className="flex items-center justify-between"><span className="flex items-center gap-1.5 text-slate-500"><Dot s={st} /> {l}</span><span className="font-bold text-[#0A234A]">{v}</span></div>
                  ))}
                </div>
                <div className="mt-3"><div className="mb-1 flex justify-between text-[10px] font-semibold text-slate-400"><span>Utilisation</span><span>{util}%</span></div><div className="h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-[#0A234A]" style={{ width: `${util}%` }} /></div></div>
              </Card>
            );
          })}
        </div>
        <YardActivity data={data} terminal={terminal} />
      </div>
    </>
  );
}

function Release({ data, reload }) {
  const [site, setSite] = useState(data.sites[0]?.id || "north");
  const [picked, setPicked] = useState([]);
  const [form, setForm] = useState({ vehicles: "Class 8 tractor-trailer", rate_b2b: 1700, rate_b2c: 45, access: "24/7 gated · PIN entry · CCTV", reclaim_days: 30, start_date: "2026-06-01", end_date: "2026-12-31" });
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState("");
  useEffect(() => { setPicked([]); }, [site]);
  const stalls = data.stalls.filter((s) => s.site === site);
  const toggle = (code, status) => { if (status !== "available") return; setPicked((p) => p.includes(code) ? p.filter((x) => x !== code) : [...p, code]); };
  const doReclaim = async (s) => {
    try { const r = await soApi.reclaim({ site: s.site, stall: s.code }); setMsg(r.reclaimed === "immediate" ? `Reclaimed ${s.code} — back in your private inventory.` : `Reclaim scheduled for ${s.code}, effective ${r.effective}. New bookings are blocked.`); await reload(); }
    catch (e) { setMsg(e.response?.data?.detail || "Could not reclaim."); }
  };
  const submit = async () => {
    if (!picked.length) { setMsg("Select at least one open stall."); return; }
    setBusy(true); setMsg("");
    try { const r = await soApi.release({ site, stalls: picked, ...form }); setMsg(`Released ${r.released} stall(s) to the marketplace.`); setPicked([]); await reload(); }
    catch (e) { setMsg(e.response?.data?.detail || "Could not release capacity."); }
    finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <>
      <Head title="Release Capacity" sub="List idle stalls for sale. Existing leases are protected and cannot be selected — you stay in control of what FleetPods sells." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <Card className="p-6 lg:col-span-3" testid="so-release-stalls">
          <div className="mb-4 flex items-center gap-3"><Label>Site</Label><div className="flex gap-1.5">{data.sites.map((s) => (
            <button key={s.id} data-testid={`so-release-site-${s.id}`} onClick={() => setSite(s.id)} className={`rounded-lg px-3 py-1.5 text-sm font-bold ${site === s.id ? "bg-[#0A234A] text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{s.name}</button>
          ))}</div></div>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {stalls.map((s) => {
              const on = picked.includes(s.code);
              const canReclaim = s.status === "released" || s.status === "booked" || s.status === "onsite";
              return (
                <div key={s.code} className={`rounded-lg border p-2.5 ${on ? "border-[#FF5722] bg-orange-50" : s.status === "leased" ? "border-slate-100 bg-slate-50" : "border-slate-200"}`}>
                  <div className="flex items-center justify-between"><span className="font-mono text-sm font-bold text-[#0A234A]">{s.code}</span><Dot s={s.status} /></div>
                  <div className="mt-1.5 flex items-center justify-between gap-1">
                    <span className="text-[11px] font-semibold capitalize text-slate-400">{s.reclaim_at ? `reclaim ${s.reclaim_at}` : s.status === "available" ? "open" : s.status}</span>
                    {s.status === "available" && <button data-testid={`so-stall-${s.code}`} onClick={() => toggle(s.code, s.status)} className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${on ? "bg-[#FF5722] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{on ? "Selected" : "Select"}</button>}
                    {canReclaim && !s.reclaim_at && <button data-testid={`so-reclaim-${s.code}`} onClick={() => doReclaim(s)} className="rounded px-1.5 py-0.5 text-[10px] font-bold text-[#FF5722] hover:bg-orange-50">Reclaim</button>}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-slate-400">Open stalls can be released; released/sold stalls can be reclaimed (leases stay protected).</p>
        </Card>
        <Card className="p-6 lg:col-span-2" testid="so-release-form">
          <h2 className="mb-4 font-display text-lg font-bold text-[#0A234A]">Listing terms</h2>
          <div className="space-y-4">
            <div><Label>Permitted vehicles</Label><Field data-testid="so-release-vehicles" value={form.vehicles} onChange={set("vehicles")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>B2B rate / mo</Label><Field data-testid="so-release-rateb2b" type="number" value={form.rate_b2b} onChange={set("rate_b2b")} /></div>
              <div><Label>B2C rate / night</Label><Field data-testid="so-release-rateb2c" type="number" value={form.rate_b2c} onChange={set("rate_b2c")} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Window start</Label><Field type="date" value={form.start_date} onChange={set("start_date")} /></div>
              <div><Label>Window end</Label><Field type="date" value={form.end_date} onChange={set("end_date")} /></div>
            </div>
            <div><Label>Access rules</Label><Field data-testid="so-release-access" value={form.access} onChange={set("access")} /></div>
            <div><Label>Reclaim notice (days)</Label><Field data-testid="so-release-reclaim" type="number" value={form.reclaim_days} onChange={set("reclaim_days")} /></div>
            <div className="flex items-center justify-between pt-1"><span className="text-sm font-semibold text-slate-500">{picked.length} stall(s) selected</span><Btn primary testid="so-release-submit" onClick={submit} disabled={busy}>Release to marketplace</Btn></div>
            {msg && <p className="text-sm font-semibold text-[#0A234A]" data-testid="so-release-msg">{msg}</p>}
          </div>
        </Card>
      </div>
      <Card className="mt-6 p-6" testid="so-listings">
        <h2 className="mb-3 font-display text-lg font-bold text-[#0A234A]">Active listings</h2>
        <div className="overflow-x-auto"><table className="w-full text-left text-sm">
          <thead><tr className="border-b border-slate-200 text-[11px] font-bold uppercase tracking-wide text-slate-400"><th className="pb-2 pr-4">Site</th><th className="pb-2 pr-4">Stalls</th><th className="pb-2 pr-4">Window</th><th className="pb-2 pr-4">B2B</th><th className="pb-2 pr-4">B2C</th><th className="pb-2 pr-4">Vehicles</th><th className="pb-2">Reclaim</th></tr></thead>
          <tbody>{data.listings.map((l) => (
            <tr key={l.id} className="border-b border-slate-100 last:border-0"><td className="py-2.5 pr-4 font-semibold capitalize text-[#0A234A]">{l.site}</td><td className="py-2.5 pr-4 font-mono text-slate-600">{l.stalls.join(", ")}</td><td className="py-2.5 pr-4 text-slate-500">{l.start_date} → {l.end_date}</td><td className="py-2.5 pr-4">{money(l.rate_b2b)}</td><td className="py-2.5 pr-4">{money(l.rate_b2c)}</td><td className="py-2.5 pr-4 text-slate-500">{l.vehicles}</td><td className="py-2.5 text-slate-500">{l.reclaim_days}d</td></tr>
          ))}</tbody>
        </table></div>
      </Card>
    </>
  );
}

function Bookings({ data, reload }) {
  const released = data.stalls.filter((s) => s.status === "released");
  const [form, setForm] = useState({ channel: "B2B", stall: "", party: "", vehicle: "", nights: 1 });
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    const stall = released.find((s) => s.code === form.stall);
    if (!stall || !form.party) { setMsg("Pick a released stall and enter the party."); return; }
    setBusy(true); setMsg("");
    try { const r = await soApi.book({ channel: form.channel, site: stall.site, stall: form.stall, party: form.party, vehicle: form.vehicle, nights: form.nights }); setMsg(`Booked ${r.ref} — ${money(r.amount)}.`); setForm((f) => ({ ...f, stall: "", party: "", vehicle: "" })); await reload(); }
    catch (e) { setMsg(e.response?.data?.detail || "Could not book — the stall may already be sold."); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Head title="Bookings" sub="One shared ledger for both channels. A stall can never be sold twice." />
      <Card className="mb-6 p-6" testid="so-booking-form">
        <div className="mb-4 inline-flex rounded-lg border border-slate-200 p-0.5">
          {["B2B", "B2C"].map((c) => <button key={c} data-testid={`so-channel-${c}`} onClick={() => setForm((f) => ({ ...f, channel: c }))} className={`rounded-md px-4 py-1.5 text-sm font-bold ${form.channel === c ? "bg-[#0A234A] text-white" : "text-slate-500"}`}>{c === "B2B" ? "B2B recurring (fleet)" : "B2C overnight (driver)"}</button>)}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div><Label>Released stall</Label><Select data-testid="so-booking-stall" value={form.stall} onChange={set("stall")}><option value="">Select…</option>{released.map((s) => <option key={s.code} value={s.code}>{s.site} · {s.code}</option>)}</Select></div>
          <div><Label>{form.channel === "B2B" ? "Fleet / carrier" : "Driver"}</Label><Field data-testid="so-booking-party" value={form.party} onChange={set("party")} placeholder={form.channel === "B2B" ? "e.g. Carrier 9" : "e.g. Driver Z"} /></div>
          <div><Label>Vehicle</Label><Field data-testid="so-booking-vehicle" value={form.vehicle} onChange={set("vehicle")} placeholder="e.g. Truck 77" /></div>
          {form.channel === "B2C" ? <div><Label>Nights</Label><Field data-testid="so-booking-nights" type="number" value={form.nights} onChange={set("nights")} /></div> : <div className="flex items-end"><Btn primary testid="so-booking-submit" onClick={submit} disabled={busy}>Reserve capacity</Btn></div>}
          {form.channel === "B2C" && <div className="flex items-end"><Btn primary testid="so-booking-submit" onClick={submit} disabled={busy}>Reserve capacity</Btn></div>}
        </div>
        {msg && <p className="mt-3 text-sm font-semibold text-[#0A234A]" data-testid="so-booking-msg">{msg}</p>}
      </Card>
      <Card className="p-6" testid="so-ledger">
        <h2 className="mb-3 font-display text-lg font-bold text-[#0A234A]">Inventory ledger</h2>
        <div className="overflow-x-auto"><table className="w-full text-left text-sm">
          <thead><tr className="border-b border-slate-200 text-[11px] font-bold uppercase tracking-wide text-slate-400"><th className="pb-2 pr-4">Ref</th><th className="pb-2 pr-4">Channel</th><th className="pb-2 pr-4">Site</th><th className="pb-2 pr-4">Stall</th><th className="pb-2 pr-4">Party</th><th className="pb-2 pr-4">Amount</th><th className="pb-2">Status</th></tr></thead>
          <tbody>{data.bookings.map((b) => (
            <tr key={b.id} className="border-b border-slate-100 last:border-0" data-testid={`so-ledger-${b.ref}`}><td className="py-2.5 pr-4 font-mono text-slate-500">{b.ref}</td><td className="py-2.5 pr-4"><Pill tone={b.channel === "B2B" ? "bg-indigo-50 text-indigo-700" : "bg-teal-50 text-teal-700"}>{b.channel}</Pill></td><td className="py-2.5 pr-4 capitalize text-slate-600">{b.site}</td><td className="py-2.5 pr-4 font-mono font-semibold text-[#0A234A]">{b.stall}</td><td className="py-2.5 pr-4 text-slate-600">{b.party}</td><td className="py-2.5 pr-4 font-semibold">{money(b.amount)}</td><td className="py-2.5"><Pill tone={STATUS_TONE[b.status]}>{b.status}</Pill></td></tr>
          ))}</tbody>
        </table></div>
      </Card>
    </>
  );
}

function Arrival({ data, reload }) {
  const [busy, setBusy] = useState(null);
  const live = data.bookings.filter((b) => ["booked", "verified", "onsite"].includes(b.status));
  const log = data.bookings.filter((b) => b.entry_at).sort((a, b) => String(b.entry_at).localeCompare(String(a.entry_at)));
  const act = async (id, action) => { setBusy(id + action); try { await soApi.step(id, action); await reload(); } finally { setBusy(null); } };
  const fmt = (t) => t ? new Date(t).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
  return (
    <>
      <Head title="Arrival" sub="Gate cameras read the plate, match it to a booking and record entry or departure automatically. Staff can still verify and record by hand." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <Card className="p-6 lg:col-span-3" testid="so-arrival-queue">
          <h2 className="mb-3 font-display text-lg font-bold text-[#0A234A]">At the gate</h2>
          <ul className="space-y-2.5">
            {live.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-100 p-3" data-testid={`so-arrival-${b.ref}`}>
                <span className="font-mono text-sm font-bold text-[#0A234A]">{b.stall}</span>
                <span className="min-w-[9rem] flex-1"><span className="block truncate text-sm font-semibold text-[#0A234A]">{b.party} <span className="font-normal text-slate-400">· {b.vehicle}</span></span><span className="text-xs text-slate-400">{b.ref} · {b.channel} · {b.site}</span></span>
                <Pill tone={STATUS_TONE[b.status]}>{b.status}</Pill>
                <PlateEdit booking={b} reload={reload} />
                {b.status === "booked" && <Btn testid={`so-verify-${b.ref}`} disabled={busy} onClick={() => act(b.id, "verify")}>Verify</Btn>}
                {b.status === "verified" && <Btn primary testid={`so-entry-${b.ref}`} disabled={busy} onClick={() => act(b.id, "entry")}>Record entry</Btn>}
                {b.status === "onsite" && <Btn testid={`so-exit-${b.ref}`} disabled={busy} onClick={() => act(b.id, "exit")}>Record departure</Btn>}
              </li>
            ))}
            {live.length === 0 && <li className="text-sm text-slate-400">Nothing at the gate right now.</li>}
          </ul>
        </Card>
        <Card className="p-6 lg:col-span-2" testid="so-arrival-log">
          <h2 className="mb-3 font-display text-lg font-bold text-[#0A234A]">Audit log</h2>
          <ul className="space-y-3">
            {log.map((b) => (
              <li key={b.id} className="border-l-2 border-slate-200 pl-3 text-sm"><p className="font-semibold text-[#0A234A]">{b.stall} · {b.party}</p><p className="text-xs text-slate-400">Entry {fmt(b.entry_at)}{b.exit_at ? ` · Exit ${fmt(b.exit_at)}` : " · on site"}</p></li>
            ))}
            {log.length === 0 && <li className="text-sm text-slate-400">No recorded entries yet.</li>}
          </ul>
        </Card>
      </div>
      <GatePanel bookings={data.bookings} reload={reload} />
    </>
  );
}

function Overflow({ data, reload }) {
  const [form, setForm] = useState({ site: "north", channel: "B2C", party: "Carrier 9", vehicle: "Truck 88", nights: 2 });
  const [offer, setOffer] = useState(null); const [busy, setBusy] = useState(false); const [done, setDone] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const check = async () => { setBusy(true); setDone(""); setOffer(null); try { setOffer(await soApi.overflow({ ...form, approve: false })); } finally { setBusy(false); } };
  const approve = async () => { setBusy(true); try { const r = await soApi.overflow({ ...form, approve: true }); setDone(`Booked ${r.ref} at ${r.site} ${r.stall} — ${money(r.amount)}.`); setOffer(null); await reload(); } finally { setBusy(false); } };
  const siteName = (id) => data.sites.find((s) => s.id === id)?.name || id;
  return (
    <>
      <Head title="Overflow" sub="When a site fills, an eligible request is offered an approved stall at another site — value a single yard can't create." />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-6" testid="so-overflow-form">
          <h2 className="mb-4 font-display text-lg font-bold text-[#0A234A]">Capacity request</h2>
          <div className="space-y-4">
            <div><Label>Requested site</Label><Select data-testid="so-overflow-site" value={form.site} onChange={set("site")}>{data.sites.map((s) => <option key={s.id} value={s.id}>{s.name} — {s.available} open</option>)}</Select></div>
            <div className="grid grid-cols-2 gap-3"><div><Label>Channel</Label><Select value={form.channel} onChange={set("channel")}><option>B2B</option><option>B2C</option></Select></div><div><Label>Party</Label><Field value={form.party} onChange={set("party")} /></div></div>
            <Btn primary testid="so-overflow-check" onClick={check} disabled={busy}>Check availability</Btn>
          </div>
        </Card>
        <Card className="p-6" testid="so-overflow-result">
          <h2 className="mb-4 font-display text-lg font-bold text-[#0A234A]">Result</h2>
          {!offer && !done && <p className="text-sm text-slate-400">Run a capacity request to see the network routing.</p>}
          {done && <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-800" data-testid="so-overflow-done">{done}</div>}
          {offer && !offer.overflow && <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">Capacity available at {siteName(offer.site)} — stall {offer.stall}. No overflow needed.</div>}
          {offer && offer.overflow && offer.offered && (
            <div className="rounded-lg border border-[#FF5722]/30 bg-orange-50 p-4">
              <p className="text-sm font-bold text-[#0A234A]">{siteName(offer.requestedSite)} is full.</p>
              <p className="mt-1 text-sm text-slate-600">Approved overflow: <span className="font-bold text-[#0A234A]">{siteName(offer.site)} · {offer.stall}</span></p>
              <Btn primary testid="so-overflow-approve" className="mt-3" onClick={approve} disabled={busy}>Approve &amp; book</Btn>
            </div>
          )}
          {offer && offer.overflow && !offer.offered && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">Network is full across all sites.</div>}
        </Card>
      </div>
    </>
  );
}

function Settlement({ data, reload }) {
  const s = data.settlement;
  const [company, setCompany] = useState(data.company); const [saved, setSaved] = useState(false);
  useEffect(() => { setCompany(data.company); }, [data.company]);
  const save = async () => { await soApi.saveSettings({ company }); setSaved(true); setTimeout(() => setSaved(false), 1800); await reload(); };
  const Row = ({ label, value, sub, strong, minus }) => (
    <div className={`flex items-baseline justify-between py-3 ${strong ? "border-t-2 border-[#0A234A]" : "border-b border-slate-100"}`}>
      <span className={`${strong ? "font-display text-base font-bold text-[#0A234A]" : "text-sm text-slate-600"}`}>{label}{sub && <span className="ml-2 text-xs text-slate-400">{sub}</span>}</span>
      <span className={`font-mono ${strong ? "font-display text-2xl font-bold text-[#0A234A]" : minus ? "text-sm font-semibold text-red-600" : "text-sm font-semibold text-[#0A234A]"}`}>{minus ? "−" : ""}{money(value)}</span>
    </div>
  );
  return (
    <>
      <Head title="Settlement" sub="Reconcile the economics — receipts by channel, the beta share, verified-entry fees and site costs.">
        {saved && <span className="text-sm font-bold text-emerald-600" data-testid="so-settle-saved">Saved</span>}
      </Head>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <Card className="p-6 lg:col-span-3" testid="so-settlement">
          <Row label="Receipts — B2B (fleet)" value={s.receiptsB2B} />
          <Row label="Receipts — B2C (overnight)" value={s.receiptsB2C} />
          <Row label="Gross receipts" value={s.gross} />
          <Row label="FleetPods beta share" sub={`${Math.round(s.betaRate * 100)}%`} value={s.betaShare} minus />
          <Row label="Verified-entry fees" sub={`${s.entries} × ${money(s.perEntry)}`} value={s.entryFees} minus />
          <Row label="Site costs" sub="incremental" value={s.siteCosts} minus />
          <Row label={`${data.company} — your contribution`} value={s.contribution} strong />
        </Card>
        <div className="space-y-6 lg:col-span-2">
          <Card className="p-6" testid="so-settle-costs">
            <h2 className="mb-3 font-display text-lg font-bold text-[#0A234A]">Site cost breakdown</h2>
            <ul className="space-y-2 text-sm">{Object.entries(s.siteCostBreakdown).map(([k, v]) => (<li key={k} className="flex justify-between"><span className="capitalize text-slate-500">{k}</span><span className="font-mono font-semibold text-[#0A234A]">{money(v)}</span></li>))}</ul>
          </Card>
          <Card className="p-6" testid="so-settle-owner">
            <h2 className="mb-3 font-display text-lg font-bold text-[#0A234A]">Owner</h2>
            <Label>Company</Label><Field data-testid="so-settle-company" value={company} onChange={(e) => setCompany(e.target.value)} />
            <Btn primary testid="so-settle-save" className="mt-3" onClick={save}>Save</Btn>
          </Card>
        </div>
      </div>
    </>
  );
}

function Calendar({ data }) {
  const parse = (s) => { const [y, m, d] = String(s).split("-").map(Number); return new Date(y, (m || 1) - 1, d || 1); };
  const [site, setSite] = useState("all");
  const [sel, setSel] = useState(null);
  const bookings = data.bookings.filter((b) => b.status !== "cancelled" && (site === "all" || b.site === site));
  const totalStalls = data.stalls.filter((s) => site === "all" || s.site === site).length;
  const covers = (b, dt) => { const s = parse(b.start_date); const e = b.channel === "B2C" ? new Date(s.getFullYear(), s.getMonth(), s.getDate() + (b.nights || 1)) : parse(b.end_date); return dt >= new Date(s.getFullYear(), s.getMonth(), s.getDate()) && (b.channel === "B2C" ? dt < e : dt <= new Date(e.getFullYear(), e.getMonth(), e.getDate())); };
  const starts = bookings.map((b) => parse(b.start_date).getTime()).filter((n) => !isNaN(n));
  const base = starts.length ? new Date(Math.min(...starts)) : new Date();
  const [cursor, setCursor] = useState(new Date(base.getFullYear(), base.getMonth(), 1));
  const y = cursor.getFullYear(), m = cursor.getMonth();
  const firstDow = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
  while (cells.length % 7 !== 0) cells.push(null);
  const dayBookings = (dt) => bookings.filter((b) => covers(b, dt));
  const occ = (dt) => new Set(dayBookings(dt).map((b) => `${b.site}:${b.stall}`)).size;
  const heat = (n) => { if (!totalStalls || n === 0) return "bg-white"; const r = n / totalStalls; if (r < 0.25) return "bg-emerald-50"; if (r < 0.5) return "bg-emerald-100/80"; if (r < 0.75) return "bg-amber-100"; return "bg-red-100"; };
  const iso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  const today = new Date(); const todayIso = iso(today);
  const selList = sel ? dayBookings(sel) : [];
  const monthLabel = cursor.toLocaleDateString([], { month: "long", year: "numeric" });
  const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const nav = (delta) => { setSel(null); setCursor(new Date(y, m + delta, 1)); };
  return (
    <>
      <Head title="Occupancy Calendar" sub="A month at a glance — each day shaded by how full your yards are. Tap any day to see its bookings." />
      <Card className="p-6" testid="so-calendar">
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            <button data-testid="so-cal-prev" onClick={() => nav(-1)} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-lg font-bold text-slate-500 transition-colors hover:bg-slate-50 hover:text-[#0A234A]">‹</button>
            <button data-testid="so-cal-next" onClick={() => nav(1)} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-lg font-bold text-slate-500 transition-colors hover:bg-slate-50 hover:text-[#0A234A]">›</button>
          </div>
          <h2 className="font-display text-xl font-bold tracking-tight text-[#0A234A]" data-testid="so-cal-month">{monthLabel}</h2>
          <button data-testid="so-cal-today" onClick={() => { setSel(null); setCursor(new Date(today.getFullYear(), today.getMonth(), 1)); }} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-500 transition-colors hover:bg-slate-50 hover:text-[#0A234A]">Today</button>
          <select data-testid="so-cal-site" value={site} onChange={(e) => { setSite(e.target.value); setSel(null); }}
            className="ml-auto h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-bold text-[#0A234A] outline-none transition-colors hover:border-slate-300 focus:border-[#FF5722]/60 focus:ring-4 focus:ring-[#FF5722]/10">
            <option value="all">All yards</option>
            {data.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-7 overflow-hidden rounded-xl border border-slate-200">
          {DOW.map((d) => <div key={d} className="border-b border-slate-200 bg-slate-50 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-slate-400">{d}</div>)}
          {cells.map((dt, i) => {
            if (!dt) return <div key={i} className="min-h-[76px] border-b border-r border-slate-100 bg-slate-50/40 last:border-r-0" />;
            const n = occ(dt); const dIso = iso(dt); const isToday = dIso === todayIso; const isSel = sel && iso(sel) === dIso;
            const pct = totalStalls ? Math.round((n / totalStalls) * 100) : 0;
            return (
              <button key={i} data-testid={`so-cal-day-${dIso}`} onClick={() => setSel(isSel ? null : dt)}
                className={`relative min-h-[76px] border-b border-r border-slate-100 p-1.5 text-left transition-all last:border-r-0 ${heat(n)} ${isSel ? "z-10 ring-2 ring-[#FF5722]" : "hover:ring-2 hover:ring-slate-200"}`}>
                <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${isToday ? "bg-[#0A234A] text-white" : "text-slate-500"}`}>{dt.getDate()}</span>
                {n > 0 && (
                  <span className="mt-1 block">
                    <span className="inline-block rounded px-1.5 py-0.5 text-[10px] font-bold text-[#0A234A]">{n}/{totalStalls} · {pct}%</span>
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-semibold text-slate-500">
          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Occupancy</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border border-slate-200 bg-white" /> Open</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-emerald-100/80" /> Light</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-amber-100" /> Busy</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-red-100" /> Full</span>
        </div>
        {sel && (
          <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50/70 p-4" data-testid="so-cal-detail">
            <h3 className="mb-3 font-display text-base font-bold text-[#0A234A]">{sel.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })} · {selList.length} booking{selList.length === 1 ? "" : "s"}</h3>
            {selList.length === 0 ? <p className="text-sm text-slate-400">No bookings on this day — every stall is open.</p> : (
              <ul className="space-y-2">
                {selList.map((b) => (
                  <li key={b.id} className="flex flex-wrap items-center gap-3 text-sm" data-testid={`so-cal-booking-${b.ref}`}>
                    <span className="w-14 flex-shrink-0 rounded-md border border-slate-200 bg-white py-1 text-center font-mono text-xs font-bold text-[#0A234A]">{b.stall}</span>
                    <span className="min-w-0 flex-1 truncate font-semibold text-[#0A234A]">{b.party} <span className="font-normal text-slate-400">· {b.vehicle || "—"}</span></span>
                    <Pill tone={b.channel === "B2B" ? "bg-indigo-50 text-indigo-700" : "bg-teal-50 text-teal-700"}>{b.channel}</Pill>
                    <span className="font-mono text-xs capitalize text-slate-400">{b.site}</span>
                    <Pill tone={STATUS_TONE[b.status]}>{b.status}</Pill>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Card>
    </>
  );
}

function Billing({ data, reload }) {
  const [busy, setBusy] = useState(null);
  const invoices = data.bookings.filter((b) => b.status !== "cancelled");
  const collected = invoices.filter((i) => i.paid).reduce((a, b) => a + Number(b.amount || 0), 0);
  const outstanding = invoices.filter((i) => !i.paid).reduce((a, b) => a + Number(b.amount || 0), 0);
  const toggle = async (b) => { setBusy(b.id); try { await soApi.step(b.id, b.paid ? "unpay" : "pay"); await reload(); } finally { setBusy(null); } };
  const download = () => {
    const rows = [["Ref", "Party", "Channel", "Site", "Stall", "Amount", "Status"], ...invoices.map((i) => [i.ref, i.party, i.channel, i.site, i.stall, i.amount, i.paid ? "Paid" : "Unpaid"])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "fleetpods-statement.csv"; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <>
      <Head title="Billing &amp; Invoices" sub="Every booking is an invoice. Mark receipts paid and export a statement for finance.">
        <Btn testid="so-billing-export" onClick={download}>Download statement</Btn>
      </Head>
      <div className="mb-6 grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-3">
        {[["Collected", collected, "text-emerald-600"], ["Outstanding", outstanding, "text-amber-600"], ["Invoiced total", collected + outstanding, "text-[#0A234A]"]].map(([l, v, c]) => (
          <div key={l} className="bg-white p-5"><Label>{l}</Label><p className={`font-display text-3xl font-bold ${c}`}>{money(v)}</p></div>
        ))}
      </div>
      <Card className="p-6" testid="so-billing">
        <div className="overflow-x-auto"><table className="w-full text-left text-sm">
          <thead><tr className="border-b border-slate-200 text-[11px] font-bold uppercase tracking-wide text-slate-400"><th className="pb-2 pr-4">Invoice</th><th className="pb-2 pr-4">Party</th><th className="pb-2 pr-4">Channel</th><th className="pb-2 pr-4">Stall</th><th className="pb-2 pr-4">Amount</th><th className="pb-2 pr-4">Status</th><th className="pb-2">Action</th></tr></thead>
          <tbody>{invoices.map((i) => (
            <tr key={i.id} className="border-b border-slate-100 last:border-0" data-testid={`so-invoice-${i.ref}`}>
              <td className="py-2.5 pr-4 font-mono text-slate-500">{i.ref}</td><td className="py-2.5 pr-4 text-slate-600">{i.party}</td>
              <td className="py-2.5 pr-4"><Pill tone={i.channel === "B2B" ? "bg-indigo-50 text-indigo-700" : "bg-teal-50 text-teal-700"}>{i.channel}</Pill></td>
              <td className="py-2.5 pr-4 font-mono font-semibold text-[#0A234A]">{i.stall}</td><td className="py-2.5 pr-4 font-semibold">{money(i.amount)}</td>
              <td className="py-2.5 pr-4"><Pill tone={i.paid ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}>{i.paid ? "Paid" : "Unpaid"}</Pill></td>
              <td className="py-2.5"><button data-testid={`so-invoice-toggle-${i.ref}`} disabled={busy === i.id} onClick={() => toggle(i)} className="rounded px-2 py-1 text-xs font-bold text-[#FF5722] hover:bg-orange-50 disabled:opacity-40">{i.paid ? "Mark unpaid" : "Mark paid"}</button></td>
            </tr>
          ))}</tbody>
        </table></div>
      </Card>
    </>
  );
}

function Analytics({ data }) {
  const s = data.settlement;
  const active = data.bookings.filter((b) => b.status !== "cancelled");
  const bySite = data.sites.map((x) => ({ name: x.name, rev: active.filter((b) => b.site === x.id).reduce((a, b) => a + Number(b.amount || 0), 0), util: x.total ? Math.round(((x.reserved + x.occupied) / x.total) * 100) : 0 }));
  const maxRev = Math.max(1, ...bySite.map((x) => x.rev));
  const Bar = ({ label, value, max, tone }) => (
    <div><div className="mb-1 flex justify-between text-sm"><span className="font-semibold text-slate-600">{label}</span><span className="font-mono font-bold text-[#0A234A]">{money(value)}</span></div><div className="h-2.5 overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${tone}`} style={{ width: `${(value / max) * 100}%` }} /></div></div>
  );
  return (
    <>
      <Head title="Analytics" sub="Revenue mix, revenue by site and utilisation across the network." />
      <div className="mb-6 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-4">
        {[["Gross receipts", money(s.gross), "text-[#0A234A]"], ["B2B", money(s.receiptsB2B), "text-indigo-600"], ["B2C", money(s.receiptsB2C), "text-teal-600"], ["Your contribution", money(s.contribution), "text-emerald-600"]].map(([l, v, c]) => (
          <div key={l} className="bg-white p-5"><Label>{l}</Label><p className={`font-display text-2xl font-bold ${c}`}>{v}</p></div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-6" testid="so-analytics-revenue"><h2 className="mb-4 font-display text-lg font-bold text-[#0A234A]">Revenue by site</h2><div className="space-y-4">{bySite.map((x) => <Bar key={x.name} label={x.name} value={x.rev} max={maxRev} tone="bg-[#0A234A]" />)}</div></Card>
        <Card className="p-6" testid="so-analytics-util"><h2 className="mb-4 font-display text-lg font-bold text-[#0A234A]">Utilisation by site</h2><div className="space-y-4">{bySite.map((x) => (
          <div key={x.name}><div className="mb-1 flex justify-between text-sm"><span className="font-semibold text-slate-600">{x.name}</span><span className="font-bold text-[#0A234A]">{x.util}%</span></div><div className="h-2.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-[#FF5722]" style={{ width: `${x.util}%` }} /></div></div>
        ))}</div></Card>
      </div>
    </>
  );
}

function Customers({ data }) {
  const map = {};
  for (const b of data.bookings) {
    if (b.status === "cancelled") continue;
    const k = b.party; if (!map[k]) map[k] = { name: k, channel: b.channel, bookings: 0, spend: 0, sites: new Set(), vehicles: new Set() };
    map[k].bookings++; map[k].spend += Number(b.amount || 0); map[k].sites.add(b.site); if (b.vehicle) map[k].vehicles.add(b.vehicle);
  }
  const rows = Object.values(map).sort((a, b) => b.spend - a.spend);
  const b2b = rows.filter((r) => r.channel === "B2B").length;
  return (
    <>
      <Head title="Customers" sub="Every fleet and driver who has booked with you — spend, volume and sites used." />
      <div className="mb-6 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-4">
        {[["Accounts", rows.length, "text-[#0A234A]"], ["Fleets (B2B)", b2b, "text-indigo-600"], ["Drivers (B2C)", rows.length - b2b, "text-teal-600"], ["Total spend", money(rows.reduce((a, r) => a + r.spend, 0)), "text-emerald-600"]].map(([l, v, c]) => (
          <div key={l} className="bg-white p-5"><Label>{l}</Label><p className={`font-display text-3xl font-bold ${c}`}>{v}</p></div>
        ))}
      </div>
      <Card className="p-6" testid="so-customers">
        <div className="overflow-x-auto"><table className="w-full text-left text-sm">
          <thead><tr className="border-b border-slate-200 text-[11px] font-bold uppercase tracking-wide text-slate-400"><th className="pb-2 pr-4">Account</th><th className="pb-2 pr-4">Type</th><th className="pb-2 pr-4">Bookings</th><th className="pb-2 pr-4">Vehicles</th><th className="pb-2 pr-4">Sites</th><th className="pb-2">Lifetime spend</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.name} className="border-b border-slate-100 last:border-0" data-testid={`so-customer-${r.name.replace(/\s/g, "-").toLowerCase()}`}>
              <td className="py-2.5 pr-4"><span className="flex items-center gap-2.5"><span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0A234A] text-xs font-bold text-white">{r.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}</span><span className="font-semibold text-[#0A234A]">{r.name}</span></span></td>
              <td className="py-2.5 pr-4"><Pill tone={r.channel === "B2B" ? "bg-indigo-50 text-indigo-700" : "bg-teal-50 text-teal-700"}>{r.channel}</Pill></td>
              <td className="py-2.5 pr-4 font-semibold text-[#0A234A]">{r.bookings}</td>
              <td className="py-2.5 pr-4 font-mono text-slate-500">{[...r.vehicles].join(", ") || "—"}</td>
              <td className="py-2.5 pr-4 capitalize text-slate-500">{[...r.sites].join(", ")}</td>
              <td className="py-2.5 font-semibold">{money(r.spend)}</td>
            </tr>
          ))}</tbody>
        </table></div>
      </Card>
    </>
  );
}

function Maintenance({ data, reload }) {
  const [site, setSite] = useState(data.sites[0]?.id || "north");
  const [busy, setBusy] = useState(null); const [msg, setMsg] = useState("");
  const stalls = data.stalls.filter((s) => s.site === site);
  const act = async (s, action) => { setBusy(s.code); setMsg(""); try { await soApi.maintenance({ site: s.site, stall: s.code, action, reason: "Scheduled maintenance" }); await reload(); } catch (e) { setMsg(e.response?.data?.detail || "Could not update."); } finally { setBusy(null); } };
  const oosCount = data.stalls.filter((s) => s.status === "oos").length;
  return (
    <>
      <Head title="Maintenance" sub={`Take open stalls out of service for repairs and restore them when ready. ${oosCount} currently out of service.`} />
      <Card className="p-6" testid="so-maintenance">
        <div className="mb-4 flex gap-1.5">{data.sites.map((s) => (
          <button key={s.id} data-testid={`so-maint-site-${s.id}`} onClick={() => setSite(s.id)} className={`rounded-lg px-3 py-1.5 text-sm font-bold ${site === s.id ? "bg-[#0A234A] text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{s.name}</button>
        ))}</div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {stalls.map((s) => {
            const open = s.status === "available" || s.status === "released";
            const oos = s.status === "oos";
            return (
              <div key={s.code} className={`rounded-lg border p-2.5 ${oos ? "border-slate-300 bg-slate-100" : "border-slate-200"}`}>
                <div className="flex items-center justify-between"><span className="font-mono text-sm font-bold text-[#0A234A]">{s.code}</span><Dot s={s.status} /></div>
                <div className="mt-1.5 flex items-center justify-between gap-1">
                  <span className="truncate text-[11px] font-semibold capitalize text-slate-400">{oos ? (s.note || "out of service") : s.status === "available" ? "open" : s.status}</span>
                  {open && <button data-testid={`so-maint-out-${s.code}`} disabled={busy === s.code} onClick={() => act(s, "out")} className="rounded px-1.5 py-0.5 text-[10px] font-bold text-slate-600 hover:bg-slate-200 disabled:opacity-40">Take out</button>}
                  {oos && <button data-testid={`so-maint-restore-${s.code}`} disabled={busy === s.code} onClick={() => act(s, "restore")} className="rounded px-1.5 py-0.5 text-[10px] font-bold text-emerald-600 hover:bg-emerald-50 disabled:opacity-40">Restore</button>}
                </div>
              </div>
            );
          })}
        </div>
        {msg && <p className="mt-3 text-sm font-semibold text-red-600" data-testid="so-maint-msg">{msg}</p>}
      </Card>
    </>
  );
}

function Alerts({ data, setActive }) {
  const unpaid = data.bookings.filter((b) => b.status !== "cancelled" && !b.paid);
  const fullSites = data.sites.filter((s) => s.available === 0);
  const reclaims = data.stalls.filter((s) => s.reclaim_at);
  const pending = data.bookings.filter((b) => b.status === "booked");
  const oos = data.stalls.filter((s) => s.status === "oos");
  const items = [
    unpaid.length && { tone: "border-amber-200 bg-amber-50", title: `${unpaid.length} unpaid invoice${unpaid.length > 1 ? "s" : ""}`, sub: `${money(unpaid.reduce((a, b) => a + Number(b.amount || 0), 0))} outstanding`, go: "billing" },
    fullSites.length && { tone: "border-red-200 bg-red-50", title: `${fullSites.length} site${fullSites.length > 1 ? "s" : ""} at capacity`, sub: `${fullSites.map((s) => s.name).join(", ")} — route overflow to another site`, go: "overflow" },
    pending.length && { tone: "border-blue-200 bg-blue-50", title: `${pending.length} arrival${pending.length > 1 ? "s" : ""} awaiting check-in`, sub: "Verify and record entry at the gate", go: "arrival" },
    reclaims.length && { tone: "border-orange-200 bg-orange-50", title: `${reclaims.length} scheduled reclaim${reclaims.length > 1 ? "s" : ""}`, sub: reclaims.map((s) => `${s.code} by ${s.reclaim_at}`).join(" · "), go: "release" },
    oos.length && { tone: "border-slate-300 bg-slate-100", title: `${oos.length} stall${oos.length > 1 ? "s" : ""} out of service`, sub: oos.map((s) => s.code).join(", "), go: "maintenance" },
  ].filter(Boolean);
  return (
    <>
      <Head title="Alerts" sub="Everything that needs your attention across the network, in one place." />
      <div className="space-y-3" data-testid="so-alerts">
        {items.map((a, i) => (
          <button key={i} data-testid={`so-alert-${a.go}`} onClick={() => setActive(a.go)} className={`flex w-full items-center justify-between rounded-xl border ${a.tone} p-4 text-left transition-transform hover:translate-x-0.5`}>
            <div><p className="font-display text-base font-bold text-[#0A234A]">{a.title}</p><p className="mt-0.5 text-sm text-slate-500">{a.sub}</p></div>
            <span className="text-sm font-bold text-[#FF5722]">Open →</span>
          </button>
        ))}
        {items.length === 0 && <Card className="p-8 text-center text-sm text-slate-400">All clear — nothing needs attention.</Card>}
      </div>
    </>
  );
}

export default function SiteOwnerConsole() {
  const { user, logout } = useAuth();
  const [active, setActive] = useState("portfolio");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [resetting, setResetting] = useState(false);

  const authed = user && (user.role === "site_operator" || user.role === "admin");
  const load = useCallback(async () => {
    try { setData(await soApi.state()); setError(""); }
    catch (e) { setError(e.response?.data?.detail || "Could not load site data."); }
  }, []);
  useEffect(() => { if (authed) load(); }, [authed, load]);

  const doReset = async () => { setResetting(true); try { await soApi.reset(); await load(); } finally { setResetting(false); } };
  const signOut = async () => { await logout?.(); };

  const screens = data && {
    portfolio: <Portfolio data={data} selectedBay={null} setSelectedBay={() => {}} />, release: <Release data={data} reload={load} />, bookings: <Bookings data={data} reload={load} />,
    customers: <Customers data={data} />,
    calendar: <Calendar data={data} />,
    arrival: <Arrival data={data} reload={load} />, overflow: <Overflow data={data} reload={load} />,
    maintenance: <Maintenance data={data} reload={load} />,
    billing: <Billing data={data} reload={load} />, analytics: <Analytics data={data} />,
    alerts: <Alerts data={data} setActive={setActive} />,
    settlement: <Settlement data={data} reload={load} />,
    security: <SecurityCenter />,
  };

  if (user === undefined) return <div className="so-bg flex min-h-[100dvh] items-center justify-center text-sm font-semibold text-slate-400" data-testid="so-boot">Loading…</div>;
  if (!authed) return <Navigate to="/dashboard" replace />;

  return (
    <div data-testid="siteowner-console" className="so-bg min-h-[100dvh] text-[#0A234A]">
      <header className="sticky top-0 z-40 flex h-16 items-center gap-5 border-b border-slate-200/70 bg-white/80 px-6 backdrop-blur-xl">
        <span className="font-display text-lg font-extrabold tracking-tight text-[#0A234A]">FLEET<span className="text-[#FF5722]">PODS</span></span>
        <span className="hidden text-sm font-bold text-slate-500 md:inline" data-testid="so-company-name">{data?.company || "Sitepods"}</span>
        <span className="hidden rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 sm:inline" data-testid="so-demo-badge">Sandbox data</span>
        <div className="ml-auto flex items-center gap-4 text-sm font-semibold">
          <button data-testid="so-reset" onClick={doReset} disabled={resetting} className="text-slate-400 hover:text-[#0A234A] disabled:opacity-40">{resetting ? "Resetting…" : "Reset demo"}</button>
          <button data-testid="so-user" onClick={signOut} className="text-[#0A234A] hover:text-[#FF5722]">Sign out</button>
        </div>
      </header>

      <div className="flex">
        <aside className="sticky top-16 hidden h-[calc(100dvh-64px)] w-56 flex-shrink-0 border-r border-slate-200/70 bg-white/70 p-4 backdrop-blur-xl lg:block">
          <nav className="space-y-1">
            {NAV.map((n, i) => (
              <button key={n.id} data-testid={`so-nav-${n.id}`} onClick={() => setActive(n.id)}
                className={`group relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold transition-all duration-200 ${active === n.id ? "bg-[#0A234A] text-white shadow-[0_12px_26px_-14px_rgba(10,35,74,0.85)]" : "text-slate-500 hover:bg-white hover:text-[#0A234A] hover:shadow-[0_2px_12px_-6px_rgba(10,35,74,0.25)]"}`}>
                {active === n.id && <span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-[#FF5722]" />}
                <span className={`font-mono text-xs ${active === n.id ? "text-[#FF5722]" : "text-slate-300 group-hover:text-slate-400"}`}>{String(i + 1).padStart(2, "0")}</span> {n.label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="lg:hidden"><div className="fixed bottom-0 left-0 right-0 z-40 flex overflow-x-auto border-t border-slate-200 bg-white px-2 py-2">
          {NAV.map((n) => <button key={n.id} data-testid={`so-mnav-${n.id}`} onClick={() => setActive(n.id)} className={`flex-shrink-0 rounded-lg px-3 py-1.5 text-xs font-bold ${active === n.id ? "bg-[#0A234A] text-white" : "text-slate-500"}`}>{n.label}</button>)}
        </div></div>

        <main className="min-w-0 flex-1 p-6 pb-24 lg:p-10 lg:pb-10">
          {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-600" data-testid="so-error">{error}</div>}
          {!data && !error && <div className="flex h-[60vh] items-center justify-center text-sm font-semibold text-slate-400" data-testid="so-loading">Loading…</div>}
          {data && <div key={active} className="so-anim">{screens[active]}</div>}
        </main>
      </div>
    </div>
  );
}
